import { useCallback, useEffect, useRef, useState } from "react";
import {
  createSrsItem,
  isDue,
  qualityFromAccuracy,
  scheduleReview,
  type SrsItem,
} from "@onchainreps/srs";
import { LocalStore } from "@onchainreps/storage";
import type { DrillItem } from "./drills";

export interface DrillSessionState {
  index: number;
  total: number;
  solved: number;
  streak: number;
  bestStreak: number;
  finished: boolean;
  lastQuality: number | null;
}

const INITIAL_STATE: DrillSessionState = {
  index: 0,
  total: 0,
  solved: 0,
  streak: 0,
  bestStreak: 0,
  finished: false,
  lastQuality: null,
};

export interface DrillSession extends DrillSessionState {
  current: DrillItem | null;
  answer: (correct: boolean, attempts: number) => void;
  next: () => void;
  restart: () => void;
}

export function useDrillSession(items: DrillItem[], store: LocalStore | null): DrillSession {
  const [state, setState] = useState<DrillSessionState>(INITIAL_STATE);
  const srsRef = useRef<Map<string, SrsItem>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setState({ ...INITIAL_STATE, total: items.length });

    void (async () => {
      if (!store) return;
      const rows = await store.getSrsItems<SrsItem>();
      if (cancelled) return;
      const map = new Map<string, SrsItem>();
      for (const row of rows) map.set(row.id, row);
      srsRef.current = map;
    })();

    return () => {
      cancelled = true;
    };
  }, [items, store]);

  const answer = useCallback(
    (correct: boolean, attempts: number) => {
      const item = items[state.index];
      if (!item) return;

      const accuracy = correct ? (attempts === 1 ? 100 : attempts === 2 ? 85 : 70) : 0;
      const quality = qualityFromAccuracy(accuracy);

      setState((current) => {
        const streak = correct ? current.streak + 1 : 0;
        return {
          ...current,
          solved: current.solved + (correct ? 1 : 0),
          streak,
          bestStreak: Math.max(current.bestStreak, streak),
          lastQuality: quality,
        };
      });

      if (!store) return;
      void (async () => {
        const existing = srsRef.current.get(item.id) ?? createSrsItem(item.id);
        const updated = scheduleReview(existing, quality);
        srsRef.current.set(item.id, updated);
        await store.putSrsItem(updated);
      })();
    },
    [items, state.index, store],
  );

  const next = useCallback(() => {
    setState((current) => ({
      ...current,
      index: current.index + 1,
      finished: current.index + 1 >= current.total,
    }));
  }, []);

  const restart = useCallback(() => {
    setState({ ...INITIAL_STATE, total: items.length });
  }, [items.length]);

  const current = items[state.index] ?? null;

  return { ...state, current, answer, next, restart };
}

export function dueDrills(items: DrillItem[], store: LocalStore | null): DrillItem[] {
  if (!store) return items;
  return items;
}

export function isSrsDue(item: SrsItem | undefined, now = new Date()): boolean {
  if (!item) return true;
  return isDue(item, now);
}
